/* MediaPicker — thin wrapper around GET /api/admin/media that opens
 * as a modal and returns either a single served-image URL (mode="single")
 * or lets the caller build up an array (mode="multi"). Used by the
 * Distribution form in UpdatesTab to attach a Featured Photo and
 * Additional Photos from the site's central Media Library instead of
 * forcing admins to paste raw URLs.
 *
 * The Media Library itself lives in /admin → Media Library and is
 * managed via MediaLibraryPanel. This picker never uploads, only
 * selects — uploads must happen in the Media Library tab. */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { X, Search, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIA_API = `${BACKEND_URL}/api/admin/media`;
const SERVE = `${BACKEND_URL}/api/media/serve`;

// Build the served URL for a Media Library item. `size` picks the
// pre-generated responsive variant; `desktop` is the largest real
// variant produced by the backend (1920px WebP). Historic default was
// "large" — that was never a real variant and returned HTTP 400. A
// backend alias still maps "large" → "desktop" so old saved URLs
// keep working, but every new URL built via this helper should use a
// valid variant name from the outset.
export const mediaServeUrl = (mediaId, size = 'desktop') =>
  `${SERVE}/${mediaId}/${size}`;

const MediaPicker = ({ open, onClose, onPick, mode = 'single', excludeUrls = [] }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await axios.get(MEDIA_API, {
        withCredentials: true,
        params: { limit: 120, status: 'active', q: q.trim() || undefined },
      });
      setItems(r.data?.rows || []);
    } catch { setItems([]); }
    setLoading(false);
  }, [q, open]);

  useEffect(() => { load(); }, [load]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-picker-modal"
    >
      <div
        className="bg-white rounded-lg p-4 w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-[#0A0A0A]">
              {mode === 'single' ? 'Pick Featured Photo' : 'Pick Additional Photos'}
            </h3>
            <p className="text-[11px] text-[#6B7280]">
              {mode === 'single'
                ? 'Choose one image. Uploads happen in the Media Library tab.'
                : 'Click to add. Selected items are marked below.'}
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="media-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search library…"
            className="w-full pl-8 pr-3 py-1.5 text-[11px] border border-[#E5E7EB] rounded"
            data-testid="media-picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-xs text-[#9CA3AF]">
              No media matches. Upload some in the Media Library tab first.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {items.map((m) => {
                const url = mediaServeUrl(m.id, 'desktop');
                const already = excludeUrls.includes(url);
                return (
                  <button key={m.id} type="button"
                    onClick={() => { if (!already) { onPick(url, m); if (mode === 'single') onClose(); } }}
                    disabled={already && mode === 'multi'}
                    className={`relative aspect-square rounded overflow-hidden border ${already ? 'border-emerald-500 opacity-60' : 'border-[#E5E7EB] hover:border-[#BA1826]'}`}
                    data-testid={`media-picker-item-${m.id}`}
                  >
                    <img src={mediaServeUrl(m.id, 'thumb')}
                      alt={m.alt_text || m.title || ''}
                      className="w-full h-full object-cover" />
                    {already && mode === 'multi' && (
                      <span className="absolute inset-0 flex items-center justify-center bg-emerald-500/40 text-white text-[9px] font-bold">
                        Added
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {mode === 'multi' && (
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={onClose}
              className="btn-primary text-xs px-3 py-1.5"
              data-testid="media-picker-done">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaPicker;
